import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import Avatar from '../../components/common/Avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  ArrowLeft, 
  Send, 
  Clock,
  AlertCircle,
  CheckCircle2,
  MessageSquare,
  User,
  Building2,
  Loader2,
  FolderKanban,
  Calendar
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TicketDetailPage = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user, token, isAgency } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTicket = async () => {
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicket(response.data);
    } catch (error) {
      console.error('Error fetching ticket:', error);
      toast.error('Erreur lors du chargement du ticket');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    setSending(true);
    try {
      await axios.post(
        `${API}/tickets/${ticketId}/reply`,
        { message: replyMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setReplyMessage('');
      fetchTicket();
      toast.success('Réponse envoyée');
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Erreur lors de l\'envoi de la réponse');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(
        `${API}/tickets/${ticketId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTicket(prev => ({ ...prev, status: newStatus }));
      toast.success('Statut mis à jour');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700 border-blue-200',
      in_progress: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      resolved: 'bg-green-100 text-green-700 border-green-200',
      closed: 'bg-gray-100 text-gray-600 border-gray-200',
    };
    const labels = {
      open: 'Ouvert',
      in_progress: 'En cours',
      resolved: 'Résolu',
      closed: 'Fermé',
    };
    return (
      <Badge variant="outline" className={cn('font-medium', styles[status] || styles.open)}>
        {status === 'resolved' && <CheckCircle2 className="w-3 h-3 mr-1" />}
        {status === 'open' && <AlertCircle className="w-3 h-3 mr-1" />}
        {labels[status] || status}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-gray-100 text-gray-600',
      normal: 'bg-blue-100 text-blue-600',
      high: 'bg-orange-100 text-orange-600',
      urgent: 'bg-red-100 text-red-600',
    };
    const labels = {
      low: 'Basse',
      normal: 'Normale',
      high: 'Haute',
      urgent: 'Urgente',
    };
    return (
      <Badge className={cn('font-medium', styles[priority] || styles.normal)}>
        {labels[priority] || priority}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!ticket) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-4 md:px-8 pb-6 pt-24 md:pt-28",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/tickets')}
          className="mb-4 -ml-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux tickets
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Messages */}
          <div className="lg:col-span-2 space-y-4">
            {/* Ticket Header */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-xl" style={{ color: theme.colors?.primary }}>
                      {ticket.subject}
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      Ticket #{ticket.id.substring(0, 8)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(ticket.status)}
                    {getPriorityBadge(ticket.priority)}
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Messages */}
            <Card>
              <CardContent className="p-4">
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {/* Initial Message */}
                  <div className="flex gap-3">
                    <Avatar 
                      name={ticket.user_name || 'Client'}
                      size="md"
                      type="client"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm">{ticket.user_name || 'Client'}</span>
                        <Badge variant="outline" className="text-xs">Client</Badge>
                        <span className="text-xs text-slate-400">
                          {formatDate(ticket.created_at)}
                        </span>
                      </div>
                      <div className="bg-slate-100 rounded-lg p-3">
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">
                          {ticket.messages?.[0]?.message || ticket.message || 'Aucun message initial'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Replies */}
                  {ticket.messages?.slice(1).map((msg, index) => {
                    const isAgencyReply = msg.user_id !== ticket.user_id;
                    return (
                      <div key={msg.id || index} className={cn("flex gap-3", isAgencyReply && "flex-row-reverse")}>
                        <Avatar 
                          name={isAgencyReply ? 'Agence' : (ticket.user_name || 'Client')}
                          size="md"
                          type={isAgencyReply ? 'agency' : 'client'}
                        />
                        <div className={cn("flex-1", isAgencyReply && "flex flex-col items-end")}>
                          <div className={cn("flex items-center gap-2 mb-1", isAgencyReply && "flex-row-reverse")}>
                            <span className="font-semibold text-sm">
                              {isAgencyReply ? 'Équipe Support' : (ticket.user_name || 'Client')}
                            </span>
                            <Badge variant="outline" className={cn(
                              "text-xs",
                              isAgencyReply ? "bg-primary/10 text-primary border-primary/20" : ""
                            )}>
                              {isAgencyReply ? 'Agence' : 'Client'}
                            </Badge>
                            <span className="text-xs text-slate-400">
                              {formatDate(msg.created_at)}
                            </span>
                          </div>
                          <div className={cn(
                            "rounded-lg p-3 max-w-[85%]",
                            isAgencyReply 
                              ? "bg-primary/10 text-slate-800" 
                              : "bg-slate-100"
                          )}>
                            <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply Form */}
                {ticket.status !== 'closed' && (
                  <form onSubmit={handleSendReply} className="mt-4 pt-4 border-t">
                    <div className="flex gap-3">
                      <Avatar 
                        name={isAgency ? 'Agence' : (user?.name || 'Moi')}
                        size="md"
                        type={isAgency ? 'agency' : 'client'}
                      />
                      <div className="flex-1">
                        <Textarea
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          placeholder="Écrivez votre réponse..."
                          className="min-h-[100px] resize-none"
                          disabled={sending}
                        />
                        <div className="flex justify-end mt-2">
                          <Button 
                            type="submit" 
                            disabled={!replyMessage.trim() || sending}
                            style={{ backgroundColor: theme.colors?.accent }}
                          >
                            {sending ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4 mr-2" />
                            )}
                            Envoyer
                          </Button>
                        </div>
                      </div>
                    </div>
                  </form>
                )}

                {ticket.status === 'closed' && (
                  <div className="mt-4 pt-4 border-t text-center text-slate-500 text-sm">
                    <CheckCircle2 className="w-5 h-5 mx-auto mb-2 text-green-500" />
                    Ce ticket est fermé. Vous ne pouvez plus y répondre.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Ticket Info */}
          <div className="space-y-4">
            {/* Status Actions */}
            {isAgency && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Changer le statut</label>
                    <Select 
                      value={ticket.status} 
                      onValueChange={handleStatusChange}
                      disabled={updatingStatus}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Ouvert</SelectItem>
                        <SelectItem value="in_progress">En cours</SelectItem>
                        <SelectItem value="resolved">Résolu</SelectItem>
                        <SelectItem value="closed">Fermé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Ticket Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Informations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <User className="w-4 h-4 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Créé par</p>
                    <p className="text-sm font-medium">{ticket.user_name || 'Client'}</p>
                  </div>
                </div>

                {ticket.project_name && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                      <FolderKanban className="w-4 h-4 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Projet</p>
                      <p className="text-sm font-medium">{ticket.project_name}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Créé le</p>
                    <p className="text-sm font-medium">{formatDate(ticket.created_at)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Messages</p>
                    <p className="text-sm font-medium">{ticket.messages?.length || 1} message(s)</p>
                  </div>
                </div>

                {ticket.updated_at && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Dernière mise à jour</p>
                      <p className="text-sm font-medium">{formatDate(ticket.updated_at)}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TicketDetailPage;
